/**
 * Simple source citation utilities
 */

interface SourceMetadata {
  documentGuid: string;
  pageNumber?: number;
  paragraphNumber?: number;
}

type CitationLocator =
  | { chunkId: number; segmentIndex?: never; cellRange?: string }
  | { segmentIndex: number; chunkId?: never; cellRange?: never }
  | { chunkId?: never; segmentIndex?: never; cellRange?: never };

export type Source = SourceMetadata & CitationLocator;

export interface Document {
  guid: string;
  displayName?: string;
  fileName?: string;
}

export const CITATION_UUID_PATTERN = '[a-f0-9]{8}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{12}';
export const CITATION_CELL_RANGE_PATTERN = '[A-Za-z]+\\d+(?::[A-Za-z]+\\d+)?';
export const CITATION_CHUNK_LOCATOR_PATTERN = `\\d+(?:@${CITATION_CELL_RANGE_PATTERN})?`;
export const CITATION_SEGMENT_LOCATOR_PATTERN = 's\\d+';
export const CITATION_EVIDENCE_LOCATOR_PATTERN = `(?:${CITATION_CHUNK_LOCATOR_PATTERN}|${CITATION_SEGMENT_LOCATOR_PATTERN})`;
// A docket citation ([cite:docket:<entry_guid>]) points at a court docket entry
// rather than a Crimson document, so it carries the `docket:` prefix to keep it
// distinct from a bare document GUID (which would otherwise open the doc panel).
export const CITATION_DOCKET_BODY_PATTERN = `docket:${CITATION_UUID_PATTERN}`;
export const CITATION_BODY_PATTERN = `(?:${CITATION_DOCKET_BODY_PATTERN}|${CITATION_UUID_PATTERN}(?::${CITATION_EVIDENCE_LOCATOR_PATTERN})?|\\d+)`;
export const CITATION_RAW_MARKER_PATTERN = `\\[cite:${CITATION_BODY_PATTERN}\\]`;
export const CITATION_PLACEHOLDER_MARKER_PATTERN = `@@crimson-cite:${CITATION_BODY_PATTERN}@@`;
export const WEB_CITATION_URL_PATTERN = 'https?:\\/\\/[^\\]\\s<>"]+';
export const WEB_CITATION_RAW_MARKER_PATTERN = `\\[web:(?:${WEB_CITATION_URL_PATTERN})\\]`;
const WEB_CITATION_PLACEHOLDER_CHARSET = 'A-Za-z0-9._~%-';
export const WEB_CITATION_PLACEHOLDER_MARKER_PATTERN = `@@crimson-web:[${WEB_CITATION_PLACEHOLDER_CHARSET}]+@@`;
export const CITATION_MARKER_PATTERN = `(?:${CITATION_RAW_MARKER_PATTERN}|${CITATION_PLACEHOLDER_MARKER_PATTERN}|${WEB_CITATION_RAW_MARKER_PATTERN}|${WEB_CITATION_PLACEHOLDER_MARKER_PATTERN})`;
export const CITATION_EVIDENCE_MARKER_PATTERN = `\\[cite:${CITATION_UUID_PATTERN}:${CITATION_EVIDENCE_LOCATOR_PATTERN}\\]`;

const UUID_PATTERN = CITATION_UUID_PATTERN;
const A1_RANGE_PATTERN = CITATION_CELL_RANGE_PATTERN;
const EVIDENCE_LOCATOR_REGEX = new RegExp(`^${CITATION_EVIDENCE_LOCATOR_PATTERN}$`, 'i');

const rawCitationRegex = () => new RegExp(CITATION_RAW_MARKER_PATTERN, 'gi');
const citationMarkerRegex = () => new RegExp(CITATION_MARKER_PATTERN, 'gi');

function chunkCitationKey(guid: string, chunkId: string | number, cellRange?: string): string {
  return `${guid}:${chunkId}${cellRange ? `@${cellRange}` : ''}`;
}

function segmentCitationKey(
  guid: string,
  segmentIndex: string | number,
): string {
  return `${guid}:s${segmentIndex}`;
}

export type ParsedCitationMarker =
  | { kind: 'legacy'; key: string; legacyNumber: string }
  | { kind: 'document'; key: string; documentGuid: string }
  | { kind: 'docket'; key: string; entryGuid: string }
  | { kind: 'chunk'; key: string; documentGuid: string; index: number; cellRange?: string }
  | {
      kind: 'segment';
      key: string;
      documentGuid: string;
      index: number;
    }
  | { kind: 'web'; key: string; url: string };

function docketCitationKey(entryGuid: string): string {
  return `docket:${entryGuid}`;
}

/** In-app URL for a docket citation: the matter Dockets tab, focused on this guid. */
export function docketCitationHref(slug: string, entryGuid: string): string {
  return `/matter/${encodeURIComponent(slug)}/dockets?entry=${encodeURIComponent(entryGuid)}`;
}

/** Parse either a raw citation or its streaming placeholder using one grammar. */
export function parseCitationMarker(marker: string): ParsedCitationMarker | null {
  const webCitation = parseWebCitationMarker(marker);
  if (webCitation) return webCitation;

  const lowerMarker = marker.toLowerCase();
  const body = lowerMarker.startsWith('@@crimson-cite:')
    ? marker.slice('@@crimson-cite:'.length, -'@@'.length)
    : lowerMarker.startsWith('[cite:')
      ? marker.slice('[cite:'.length, -']'.length)
      : '';
  if (!body) return null;
  if (/^\d+$/.test(body)) {
    return { kind: 'legacy', key: body, legacyNumber: body };
  }

  const docketMatch = body.match(new RegExp(`^docket:(${CITATION_UUID_PATTERN})$`, 'i'));
  if (docketMatch) {
    const entryGuid = docketMatch[1].toLowerCase();
    return { kind: 'docket', key: docketCitationKey(entryGuid), entryGuid };
  }

  const match = body.match(new RegExp(
    `^(${CITATION_UUID_PATTERN})(?::(?:(s)(\\d+)|(\\d+)(?:@(${CITATION_CELL_RANGE_PATTERN}))?))?$`,
    'i',
  ));
  if (!match) return null;

  const documentGuid = match[1].toLowerCase();
  if (!match[2] && !match[3] && !match[4]) {
    return { kind: 'document', key: documentGuid, documentGuid };
  }
  if (match[2]) {
    const index = Number.parseInt(match[3], 10);
    return {
      kind: 'segment',
      key: segmentCitationKey(documentGuid, index),
      documentGuid,
      index,
    };
  }

  const index = Number.parseInt(match[4], 10);
  const cellRange = match[5];
  return {
    kind: 'chunk',
    key: chunkCitationKey(documentGuid, index, cellRange),
    documentGuid,
    index,
    cellRange,
  };
}

export function citationPlaceholderBody(citation: ParsedCitationMarker): string {
  if (citation.kind === 'web') return encodeWebCitationPlaceholder(citation.url);
  if (citation.kind === 'legacy') return citation.legacyNumber;
  if (citation.kind === 'document') return citation.documentGuid;
  if (citation.kind === 'docket') return `docket:${citation.entryGuid}`;
  if (citation.kind === 'segment') return `${citation.documentGuid}:s${citation.index}`;
  return `${citation.documentGuid}:${citation.index}${citation.cellRange ? `@${citation.cellRange}` : ''}`;
}

/** Percent-encode a URL into WEB_CITATION_PLACEHOLDER_CHARSET (encodeURIComponent leaves !'()*). */
export function encodeWebCitationPlaceholder(url: string): string {
  return encodeURIComponent(url).replace(/[!'()*]/g, (char) => {
    return `%${char.charCodeAt(0).toString(16).toUpperCase()}`;
  });
}

export function parseWebCitationMarker(marker: string): Extract<ParsedCitationMarker, { kind: 'web' }> | null {
  const trimmed = marker.trim();
  const lower = trimmed.toLowerCase();
  let rawUrl = '';
  if (lower.startsWith('[web:') && trimmed.endsWith(']')) {
    rawUrl = trimmed.slice('[web:'.length, -']'.length);
  } else if (lower.startsWith('@@crimson-web:') && trimmed.endsWith('@@')) {
    try {
      rawUrl = decodeURIComponent(trimmed.slice('@@crimson-web:'.length, -'@@'.length));
    } catch {
      return null;
    }
  } else {
    return null;
  }
  const url = safeHttpUrl(rawUrl);
  if (!url) return null;
  return { kind: 'web', key: `web:${url}`, url };
}

export function safeHttpUrl(value: string): string | null {
  const candidate = value.trim();
  if (!candidate) return null;
  try {
    const parsed = new URL(candidate);
    if (parsed.protocol !== 'http:' && parsed.protocol !== 'https:') return null;
    if (parsed.username || parsed.password) return null;
    return parsed.href;
  } catch {
    return null;
  }
}

/** Assign one stable number to each citation in visible textual order. */
export function buildCitationNumberingMap(content: string): Map<string, number> {
  const numbers = new Map<string, number>();
  for (const match of content.matchAll(citationMarkerRegex())) {
    const citation = parseCitationMarker(match[0]);
    if (citation && !numbers.has(citation.key)) {
      numbers.set(citation.key, numbers.size + 1);
    }
  }
  return numbers;
}

/**
 * Validate if a citation has the correct format
 * Valid formats:
 * - [cite:GUID:chunkId] where GUID is a valid UUID and chunkId is a single number
 * - [cite:GUID:chunkId@B12:D20] for spreadsheet cell ranges
 * - [cite:GUID] where GUID is a valid UUID
 * Invalid formats:
 * - [cite:Unknown:...] - GUID is "Unknown"
 * - [cite:GUID:1-5] - range format
 * - [cite:GUID:1,2,3] - comma-separated format
 */
export function isValidCitation(guid: string, chunkId?: string): boolean {
  // Check if GUID is "Unknown" or empty
  if (!guid || guid.toLowerCase() === 'unknown') {
    return false;
  }

  // Check if GUID is a valid UUID format
  const uuidRegex = /^[a-f0-9]{8}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{12}$/i;
  if (!uuidRegex.test(guid)) {
    return false;
  }

  // If chunkId is provided, validate it
  if (chunkId !== undefined) {
    // Check if it's a single number (not a range or comma-separated)
    // Invalid: "1-5", "1,2,3", "12-13,19-21"
    if (!EVIDENCE_LOCATOR_REGEX.test(chunkId)) {
      return false;
    }
  }

  return true;
}

export function renderDeletedCitationHtml({
  cellRange,
  chunkId,
  citationKey,
  displayNumber,
  documentGuid,
  legacyNumber,
  segmentIndex,
}: {
  cellRange?: string;
  chunkId?: number;
  citationKey?: string;
  displayNumber?: string;
  documentGuid?: string;
  legacyNumber?: string;
  segmentIndex?: number;
}): string {
  const attributes: Array<[string, string]> = [
    ['class', 'citation-deleted'],
    ['data-citation-state', 'deleted'],
  ];
  if (documentGuid) attributes.push(['data-doc-guid', documentGuid]);
  if (chunkId !== undefined) attributes.push(['data-chunk-id', String(chunkId)]);
  if (segmentIndex !== undefined) attributes.push(['data-segment-index', String(segmentIndex)]);
  if (cellRange) attributes.push(['data-cell-range', cellRange]);
  if (legacyNumber) attributes.push(['data-cite', legacyNumber]);
  if (citationKey) attributes.push(['data-citation-key', citationKey]);
  if (displayNumber && displayNumber !== '?') {
    attributes.push(['data-display-number', displayNumber]);
  }

  const serialized = attributes
    .map(([name, value]) => `${name}="${escapeHtmlAttribute(value)}"`)
    .join(' ');
  return `<span ${serialized}>?</span>`;
}

function deletedCitationBadge(
  marker: string,
  citationNumbers?: Map<string, number>,
): string {
  const citation = parseCitationMarker(marker);
  if (!citation) {
    return ` ${renderDeletedCitationHtml({})} `;
  }

  const displayNumber = citationNumbers?.get(citation.key);

  return ` ${renderDeletedCitationHtml({
    cellRange: citation.kind === 'chunk' ? citation.cellRange : undefined,
    chunkId: citation.kind === 'chunk' ? citation.index : undefined,
    citationKey: citation.key,
    displayNumber: displayNumber !== undefined ? String(displayNumber) : undefined,
    documentGuid:
      citation.kind === 'document' || citation.kind === 'chunk' || citation.kind === 'segment'
        ? citation.documentGuid
        : undefined,
    legacyNumber: citation.kind === 'legacy' ? citation.legacyNumber : undefined,
    segmentIndex: citation.kind === 'segment' ? citation.index : undefined,
  })} `;
}

function escapeHtmlAttribute(value: string) {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

/**
 * Filter out invalid citations from content
 * Removes citations with "Unknown" GUID, invalid UUID format, range formats,
 * or GUIDs that don't exist in the provided documents list
 * @param content - The content to filter
 * @param documents - Optional list of documents to validate GUIDs against
 * @returns Content with invalid citations removed
 */
export function filterInvalidCitations(content: string, documents?: Document[]): string {
  // Build a Set of valid document GUIDs for O(1) lookups
  const validGuids = documents ? new Set(documents.map(doc => doc.guid)) : null;
  const citationNumbers = documents ? buildCitationNumberingMap(content) : undefined;

  // Single pass to filter all citation formats
  return content.replace(/\[cite:([^[\]]+)\]/g, (match, citationContent) => {
    // Docket citations point at a court docket entry, not a Crimson document, so
    // they are validated against the UUID grammar only and never checked against
    // (or marked deleted for) the documents list.
    if (/^docket:/i.test(citationContent)) {
      const entryGuid = citationContent.slice('docket:'.length);
      return isValidCitation(entryGuid) ? match : '';
    }

    // Check if it's chunk-level (contains a colon)
    const colonIndex = citationContent.indexOf(':');

    if (colonIndex > 0) {
      // Chunk-level citation: [cite:GUID:chunkId]
      const guid = citationContent.substring(0, colonIndex);
      const chunkId = citationContent.substring(colonIndex + 1);

      // Check if citation format is valid
      if (!isValidCitation(guid, chunkId)) {
        return '';
      }

      // If documents list provided, check if GUID exists in valid set
      if (validGuids && !validGuids.has(guid)) {
        return deletedCitationBadge(match, citationNumbers);
      }

      return match;
    } else {
      // Document-level citation: [cite:GUID]
      // Check if citation format is valid
      if (!isValidCitation(citationContent)) {
        return '';
      }

      // If documents list provided, check if GUID exists in valid set
      if (validGuids && !validGuids.has(citationContent)) {
        return deletedCitationBadge(match, citationNumbers);
      }

      return match;
    }
  });
}

/**
 * Parse citations from text
 * Auto-generates source map from inline citations (no Citations section needed)
 * Handles multiple formats:
 * - [cite:GUID:chunkId] - Chunk-level citation with paragraph info
 * - [cite:GUID] - Document-level citation
 * - [cite:N] guid - Legacy numbered format (backward compatibility)
 * Filters out invalid citations
 * @param text - The text to parse citations from
 * @param documents - Optional list of documents to validate GUIDs against
 * @returns A map of citation key (GUID:chunkId) to source information
 */
export function parseSources(text: string, documents?: Document[]): Map<string, Source> {
  const sources = new Map<string, Source>();
  const documentCitations = new Set<string>();

  for (const match of text.matchAll(rawCitationRegex())) {
    const citation = parseCitationMarker(match[0]);
    if (citation?.kind === 'chunk') {
      sources.set(citation.key, {
        documentGuid: citation.documentGuid,
        chunkId: citation.index,
        cellRange: citation.cellRange,
      });
    } else if (citation?.kind === 'segment') {
      sources.set(citation.key, {
        documentGuid: citation.documentGuid,
        segmentIndex: citation.index,
      });
    } else if (citation?.kind === 'document') {
      documentCitations.add(citation.documentGuid);
    }
  }
  for (const documentGuid of documentCitations) {
    const hasEvidence = Array.from(sources.values()).some(
      source => source.documentGuid === documentGuid,
    );
    if (!hasEvidence) sources.set(documentGuid, { documentGuid });
  }

  // Legacy: Extract Citations section if it exists (for backward compatibility)
  const citationsSectionMatch = text.match(/^## Citations\s*\n([\s\S]*)$/m);
  const citationsSection = citationsSectionMatch?.[1] || '';

  if (citationsSection && (/\[cite:\d+\]|\[cite:[a-f0-9-]{36}\]/.test(citationsSection))) {
    // Parse numbered format: [cite:N] guid or [cite:N] guid:chunkId
    const correctMatches = citationsSection.matchAll(
      new RegExp(`\\[cite:(\\d+)\\]\\s+(${UUID_PATTERN})(?::(\\d+)(?:@(${A1_RANGE_PATTERN}))?)?`, 'g')
    );
    for (const match of correctMatches) {
      const source: Source = match[3]
        ? {
            documentGuid: match[2],
            chunkId: parseInt(match[3], 10),
            cellRange: match[4],
          }
        : { documentGuid: match[2] };
      sources.set(match[1], source);
    }

    // Parse chunk-level format: [cite:GUID:chunkId] Filename (Page X, Paragraph Y)
    const chunkMatches = citationsSection.matchAll(
      new RegExp(`\\[cite:(${UUID_PATTERN}):(\\d+)(?:@(${A1_RANGE_PATTERN}))?\\][^\\n]*(?:\\(Page (\\d+)(?:, Paragraph (\\d+))?\\))?`, 'g')
    );
    for (const match of chunkMatches) {
      const guid = match[1];
      const chunkId = parseInt(match[2], 10);
      const cellRange = match[3];
      const pageNumber = match[4] ? parseInt(match[4], 10) : undefined;
      const paragraphNumber = match[5] ? parseInt(match[5], 10) : undefined;

      const key = chunkCitationKey(guid, chunkId, cellRange);
      // Update with page/paragraph info if available
      const existing = sources.get(key);
      if (existing) {
        existing.pageNumber = pageNumber;
        existing.paragraphNumber = paragraphNumber;
      }
    }
  }

  // Remove any citations that are not in the documents list
  if (documents) {
    const validGuids = new Set(documents.map(doc => doc.guid));
    sources.forEach((source, key) => {
      if (!validGuids.has(source.documentGuid)) {
        sources.delete(key);
      }
    });
  }

  return sources;
}

export type CitationInfo = { displayNumber: string } & CitationLocator;

/**
 * Extract cited documents from content with display numbers and chunk IDs
 * Returns a map of document GUID -> array of citation info (number + optional chunk ID)
 * Handles [cite:GUID:chunkId], [cite:GUID], and [cite:N] formats
 * Each citation that appears in the text is included in the result
 */
export function extractCitedDocuments(
  content: string,
  sources: Map<string, Source>,
  documents?: Document[]
): Map<string, CitationInfo[]> {
  // Keep deleted citations in the numbering pass so the source list uses the
  // same stable display numbers as the inline response. The filtered content
  // is still used when building the list, so deleted documents are omitted.
  const filteredContent = filterInvalidCitations(content, documents);

  const citationNumbers = buildCitationNumberingMap(content);

  const citationsByDoc = new Map<string, Map<string, CitationInfo>>();
  for (const match of filteredContent.matchAll(rawCitationRegex())) {
    const citation = parseCitationMarker(match[0]);
    if (!citation) continue;
    // Docket entries and web citations are not Crimson documents, so they
    // never appear in the cited-documents list.
    if (citation.kind === 'docket' || citation.kind === 'web') continue;
    const source = citation.kind === 'legacy' ? sources.get(citation.key) : undefined;
    const documentGuid = citation.kind === 'legacy'
      ? source?.documentGuid
      : citation.documentGuid;
    if (!documentGuid) continue;

    const displayNumber = String(citationNumbers.get(citation.key) ?? '?');
    const info: CitationInfo = citation.kind === 'chunk'
      ? { displayNumber, chunkId: citation.index, cellRange: citation.cellRange }
      : citation.kind === 'segment'
        ? { displayNumber, segmentIndex: citation.index }
        : source?.segmentIndex !== undefined
          ? { displayNumber, segmentIndex: source.segmentIndex }
          : source?.chunkId !== undefined
            ? { displayNumber, chunkId: source.chunkId, cellRange: source.cellRange }
            : { displayNumber };
    const documentCitations = citationsByDoc.get(documentGuid) ?? new Map();
    documentCitations.set(citation.key, info);
    citationsByDoc.set(documentGuid, documentCitations);
  }

  return new Map(Array.from(citationsByDoc, ([guid, citations]) => [
    guid,
    Array.from(citations.values()).sort(
      (a, b) => parseInt(a.displayNumber, 10) - parseInt(b.displayNumber, 10),
    ),
  ]));
}

export type WebCitationDisplay = {
  title?: string;
  siteName?: string;
};

export type WebCitationInfo = {
  displayNumber: string;
  host: string;
  url: string;
  title?: string;
  siteName?: string;
};

export function webCitationHost(url: string): string {
  try {
    return new URL(url).hostname.replace(/^www\./, '');
  } catch {
    return url;
  }
}

function clipWebCitationLabel(value: unknown, maxChars: number): string | undefined {
  if (typeof value !== 'string') return undefined;
  const clipped = value.replace(/\s+/g, ' ').trim().slice(0, maxChars);
  return clipped || undefined;
}

function ingestWebCitationDisplay(
  into: Map<string, WebCitationDisplay>,
  url: unknown,
  title?: unknown,
  siteName?: unknown,
) {
  if (typeof url !== 'string' || !url.trim()) return;
  const cleanedUrl = url.trim();
  const nextTitle = clipWebCitationLabel(title, 200);
  const nextSiteName = clipWebCitationLabel(siteName, 80);
  const existing = into.get(cleanedUrl);
  const merged: WebCitationDisplay = {
    title: nextTitle || existing?.title,
    siteName: nextSiteName || existing?.siteName,
  };
  if (!merged.title && !merged.siteName) return;
  for (const key of webCitationLookupKeys(cleanedUrl)) {
    into.set(key, merged);
  }
}

function webCitationLookupKeys(url: string): string[] {
  const keys = [url];
  try {
    const parsed = new URL(url);
    parsed.hash = '';
    keys.push(parsed.href);
    if (parsed.href.endsWith('/')) keys.push(parsed.href.slice(0, -1));
    else keys.push(`${parsed.href}/`);
  } catch {
    return keys;
  }
  return keys;
}

export function lookupWebCitationDisplay(
  meta: ReadonlyMap<string, WebCitationDisplay> | undefined,
  url: string,
): WebCitationDisplay | undefined {
  if (!meta) return undefined;
  for (const key of webCitationLookupKeys(url)) {
    const match = meta.get(key);
    if (match) return match;
  }
  return undefined;
}

function parseJsonObject(value: unknown): Record<string, unknown> | null {
  if (!value) return null;
  if (typeof value === 'object' && !Array.isArray(value)) {
    return value as Record<string, unknown>;
  }
  if (typeof value !== 'string') return null;
  try {
    const parsed = JSON.parse(value) as unknown;
    return parsed && typeof parsed === 'object' && !Array.isArray(parsed)
      ? parsed as Record<string, unknown>
      : null;
  } catch {
    return null;
  }
}

function ingestWebCitationPayload(
  into: Map<string, WebCitationDisplay>,
  payload: Record<string, unknown> | null,
) {
  if (!payload) return;
  const sidecar = payload.web_citation_meta;
  if (Array.isArray(sidecar)) {
    for (const item of sidecar) {
      if (!item || typeof item !== 'object' || Array.isArray(item)) continue;
      const row = item as Record<string, unknown>;
      ingestWebCitationDisplay(into, row.url, row.title, row.site_name ?? row.siteName);
    }
  }
  const results = payload.results;
  if (Array.isArray(results)) {
    for (const hit of results) {
      if (!hit || typeof hit !== 'object' || Array.isArray(hit)) continue;
      const row = hit as Record<string, unknown>;
      ingestWebCitationDisplay(into, row.url, row.title, row.site_name ?? row.siteName);
    }
  }
  ingestWebCitationDisplay(
    into,
    payload.url,
    payload.title,
    payload.site_name ?? payload.siteName,
  );
  ingestWebCitationDisplay(
    into,
    payload.final_url,
    payload.title,
    payload.site_name ?? payload.siteName,
  );
}

/** Titles and publisher names harvested from stored/live web-research tool output. */
export function collectWebCitationMeta(steps: unknown): Map<string, WebCitationDisplay> {
  const into = new Map<string, WebCitationDisplay>();
  const walk = (node: unknown) => {
    if (Array.isArray(node)) {
      node.forEach(walk);
      return;
    }
    if (!node || typeof node !== 'object') return;
    const record = node as Record<string, unknown>;
    const data = record.data && typeof record.data === 'object' && !Array.isArray(record.data)
      ? record.data as Record<string, unknown>
      : record;
    const fn = data.function;
    if (fn && typeof fn === 'object' && !Array.isArray(fn)) {
      ingestWebCitationPayload(into, parseJsonObject((fn as { output?: unknown }).output));
    }
    ingestWebCitationPayload(into, parseJsonObject(data.function_output));
    if (Array.isArray(data.children)) walk(data.children);
    if (Array.isArray(record.children)) walk(record.children);
  };
  walk(steps);
  return into;
}

/** Public-web cites in reading order, one row per unique URL. */
export function extractCitedWebSources(
  content: string,
  meta?: ReadonlyMap<string, WebCitationDisplay>,
): WebCitationInfo[] {
  const citationNumbers = buildCitationNumberingMap(content);
  const byUrl = new Map<string, WebCitationInfo>();
  for (const match of content.matchAll(citationMarkerRegex())) {
    const citation = parseCitationMarker(match[0]);
    if (!citation || citation.kind !== 'web' || byUrl.has(citation.url)) continue;
    const display = lookupWebCitationDisplay(meta, citation.url);
    byUrl.set(citation.url, {
      displayNumber: String(citationNumbers.get(citation.key) ?? '?'),
      host: webCitationHost(citation.url),
      url: citation.url,
      ...(display?.title ? { title: display.title } : {}),
      ...(display?.siteName ? { siteName: display.siteName } : {}),
    });
  }
  return Array.from(byUrl.values()).sort(
    (a, b) => parseInt(a.displayNumber, 10) - parseInt(b.displayNumber, 10),
  );
}

/**
 * Strip all citations from content for copying
 * Removes inline citations and optionally the Citations section
 * @param content - The content to clean
 * @param removeCitationsSection - Whether to remove the ## Citations section (default: false)
 * @returns Content with all citations removed
 */
export function stripCitationsFromContent(content: string, removeCitationsSection = false): string {
  let cleanContent = content;

  // Remove ## Citations section if requested
  if (removeCitationsSection) {
    cleanContent = cleanContent.replace(/##\s*Citations\s*[\s\S]*$/i, '');
  }

  // Remove all citation formats: [cite:GUID:chunkId], [cite:GUID], and [cite:N]
  cleanContent = cleanContent.replace(
    new RegExp(CITATION_EVIDENCE_MARKER_PATTERN, 'g'),
    ''
  ); // [cite:GUID:chunkId]
  cleanContent = cleanContent.replace(/\[cite:[a-f0-9-]{36}\]/g, ''); // [cite:GUID]
  cleanContent = cleanContent.replace(/\[cite:\d+\]/g, ''); // [cite:N] legacy format
  cleanContent = cleanContent.replace(new RegExp(WEB_CITATION_RAW_MARKER_PATTERN, 'gi'), '');
  cleanContent = cleanContent.replace(new RegExp(WEB_CITATION_PLACEHOLDER_MARKER_PATTERN, 'gi'), '');

  // Safety net: Remove any remaining malformed citations
  cleanContent = cleanContent.replace(/\[cite:[^\]]+\]/g, '');
  cleanContent = cleanContent.replace(/\[web:[^\]]+\]/g, '');

  // Remove deadline deep-link markers
  cleanContent = cleanContent.replace(/\[deadline:[a-f0-9-]{36}\]/gi, '');

  // Clean up any multiple spaces that may result from citation removal
  cleanContent = cleanContent.replace(/ {2,}/g, ' ');

  // Clean up spaces before sentence-ending punctuation that may result from citation removal
  // Only target multiple spaces before punctuation to be safe
  cleanContent = cleanContent
    .replace(/\s{2,}\./g, '.')   // Remove multiple spaces before periods
    .replace(/\s{2,},/g, ',')    // Remove multiple spaces before commas
    .replace(/\s{2,};/g, ';');   // Remove multiple spaces before semicolons

  return cleanContent;
}
